import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { Sun, Moon, Circle, X, FileCode2 } from "lucide-react";
import IdentifyPage from "./pages/IdentifyPage";
import HistoryPage from "./pages/HistoryPage";
import EvaluationPage from "./pages/EvaluationPage";
import SpeciesSearchPage from "./pages/SpeciesSearchPage";
import ComparePage from "./pages/ComparePage";
import ReviewPage from "./pages/ReviewPage";
import { getReviewQueue } from "./lib/api";
import { API } from "./lib/api";

const TABS = [
  { id: "search", file: "00_species_search.ipynb" },
  { id: "identify", file: "01_identify.ipynb" },
  { id: "history", file: "02_history.ipynb" },
  { id: "eval", file: "03_evaluation.ipynb" },
  { id: "compare", file: "04_compare.ipynb" },
  { id: "review", file: "05_review_queue.ipynb" },
];
const MENU = ["File", "Edit", "View", "Insert", "Cell", "Kernel", "Help"];

const JupyterLogo = () => (
  <div className="flex items-center gap-2" data-testid="app-logo">
    <svg viewBox="0 0 40 40" className="h-8 w-8"><circle cx="20" cy="20" r="17" fill="none" stroke="hsl(var(--jupyter))" strokeWidth="2" opacity="0.35" />
      <path d="M6 16c4-7 24-7 28 0M6 24c4 7 24 7 28 0" stroke="hsl(var(--jupyter))" strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <circle cx="31" cy="7" r="2.2" fill="hsl(var(--muted-foreground))" /><circle cx="9" cy="33" r="1.8" fill="hsl(var(--muted-foreground))" /></svg>
    <span className="text-xl font-semibold tracking-tight">moth<span className="text-[hsl(var(--jupyter))]">ai</span>py</span>
  </div>
);

function App() {
  const [tab, setTab] = useState("search");
  const [dark, setDark] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [kernel, setKernel] = useState("connecting");
  const [searchQ, setSearchQ] = useState(null);
  const [reviewCounts, setReviewCounts] = useState(null);
  useEffect(() => { getReviewQueue("pending").then((d) => setReviewCounts(d.counts)).catch(() => {}); }, [tab]);
  const explore = (name, refId) => { setSearchQ({ name, refId, t: Date.now() }); setTab("search"); };

  useEffect(() => { axios.get(`${API}/`).then(() => setKernel("idle")).catch(() => setKernel("dead")); }, []);
  const openRecord = (id) => { setOpenId(id); setTab("identify"); };
  const current = TABS.find((t) => t.id === tab);

  return (
    <div className={dark ? "dark" : ""}>
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-30 bg-card border-b shadow-sm">
          <div className="px-3 sm:px-6 h-14 flex items-center gap-3">
            <JupyterLogo />
            <div className="hidden md:block ml-3">
              <div data-testid="notebook-filename" className="text-lg leading-tight">Moth AI — uncertainty-aware identification</div>
              <div className="text-[11px] text-muted-foreground font-mono">Last checkpoint: just now (autosaved)</div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div data-testid="kernel-status" className="flex items-center gap-1.5 text-xs font-mono border rounded px-2 py-1">
                <span className="hidden sm:inline">Python 3 (moth-ai)</span>
                <Circle className={`h-3 w-3 ${kernel === "idle" ? "fill-transparent text-foreground" : kernel === "busy" ? "fill-foreground" : "text-destructive"}`} />
              </div>
              <button data-testid="theme-toggle-button" onClick={() => setDark(!dark)} className="h-8 w-8 grid place-items-center rounded border hover:bg-accent transition-colors">
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="px-3 sm:px-6 flex items-center gap-4 text-sm h-8 border-t overflow-x-auto">
            {MENU.map((m) => <span key={m} className="text-foreground/80 hover:text-foreground cursor-default">{m}</span>)}
            <span className="ml-auto hidden sm:inline text-xs text-muted-foreground font-mono">APIs: api.gbif.org · api.inaturalist.org</span>
          </div>
          <nav className="flex items-end gap-0.5 px-2 sm:px-4 bg-secondary border-t overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`${t.id}-tab-button`} onClick={() => { setTab(t.id); if (t.id === "identify") setOpenId(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-b-0 rounded-t whitespace-nowrap transition-colors ${tab === t.id ? "bg-card border-t-2 border-t-primary" : "bg-transparent border-transparent text-muted-foreground hover:text-foreground"}`}>
                <FileCode2 className="h-3.5 w-3.5 text-[hsl(var(--jupyter))]" />{t.file}{t.id === "review" && reviewCounts?.pending > 0 && <span data-testid="review-pending-badge" className="ml-1 rounded-full bg-[hsl(var(--nb-err))] text-white text-[10px] px-1.5 leading-4">{reviewCounts.pending}</span>}{tab === t.id && <X className="h-3 w-3 opacity-50" />}
              </button>
            ))}
          </nav>
        </header>
        <main className="py-4 sm:py-8 px-0 sm:px-4">
          <div data-testid={`notebook-${current.id}`} className="max-w-6xl mx-auto bg-[hsl(var(--nb-page))] sm:border shadow-sm py-4 sm:py-6 min-h-[80vh]">
            <div className={tab === "search" ? "" : "hidden"}><SpeciesSearchPage query={searchQ} setKernel={setKernel} goUpload={() => { setOpenId(null); setTab("identify"); }} /></div>
            <div className={tab === "identify" ? "" : "hidden"}><IdentifyPage openId={openId} setKernel={setKernel} onExplore={explore} /></div>
            {tab === "history" && <HistoryPage onOpen={openRecord} />}
            <div className={tab === "compare" ? "" : "hidden"}><ComparePage setKernel={setKernel} /></div>
            {tab === "review" && <ReviewPage onOpen={openRecord} active onCounts={setReviewCounts} />}
            <div className={tab === "eval" ? "" : "hidden"}><EvaluationPage onOpen={openRecord} setKernel={setKernel} active={tab === "eval"} /></div>
          </div>
        </main>
        <Toaster theme={dark ? "dark" : "light"} position="bottom-right" />
      </div>
    </div>
  );
}

export default App;
